import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { message, Modal } from "antd";
import Layout from "./common/Layout";
import "../css/editInComingOrder.scss";
import { setAPIAuthorizationToken } from "../apis/API";
import { add_modify_transaction } from "../apis/AddTransactions";
import { get_account_list } from "../apis/GetAccountList";
import Cookies from "js-cookie";

const AddInComingOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderData = location.state?.order; // 从路由 state 获取订单数据（编辑模式）
  const isEditMode = !!orderData; // 判断是否为编辑模式

  const [formData, setFormData] = useState({
    transactionNo: "",
    platformName: "",
    businessType: "电商",
    storeName: "",
    collectionAccount: "",
    merchantNo: "",
    arrivalCurrency: "EUR",
    arrivalAmount: "",
    depositCurrency: "EUR",
    depositAmount: "",
    fee: "0",
    feeCurrency: "EUR",
    status: "",
    remark: "",
    payerName: "",
    transactionTime: "",
    completionTime: "",
    note: "",
    user_id: "",
  });

  // 保存原始订单数据中的 id 和 user_id（编辑模式时必需）
  const [transactionId, setTransactionId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 收款账户列表
  const [merchantAccounts, setMerchantAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // 初始化token
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
  }, []);

  // 获取收款账户列表
  useEffect(() => {
    const fetchMerchantAccounts = async () => {
      setLoadingAccounts(true);
      try {
        const response = await get_account_list("", 1, 1000); // 获取所有账户，不分页
        if (response.data && response.data.code === 200) {
          setMerchantAccounts(response.data.data?.list || []);
          console.log("获取收款账户列表成功", response.data.data?.list);
        } else {
          message.error("获取收款账户列表失败");
        }
      } catch (error) {
        console.error("获取收款账户列表失败:", error);
        message.error("获取收款账户列表失败");
      } finally {
        setLoadingAccounts(false);
      }
    };

    fetchMerchantAccounts();
  }, []);

  // 根据入账金额和手续费自动计算到账金额：到账金额 = 入账金额 - 手续费
  useEffect(() => {
    setFormData((prev) => {
      const deposit = parseFloat(prev.depositAmount);
      const fee = parseFloat(prev.fee);
      if (!isNaN(deposit) && !isNaN(fee)) {
        const calc = (deposit - fee).toFixed(2);
        if (prev.arrivalAmount !== calc) {
          return { ...prev, arrivalAmount: calc };
        }
      } else if (prev.arrivalAmount !== "") {
        return { ...prev, arrivalAmount: "" };
      }
      return prev;
    });
  }, [formData.depositAmount, formData.fee]);

  // 填充表单数据（编辑模式）
  useEffect(() => {
    if (isEditMode && orderData) {
      // 保存 id 和 user_id（编辑时必需）
      setTransactionId(orderData.id || orderData.transactionId || null);
      setUserId(orderData.user_id || orderData.userId || null);

      const merchantNo =
        orderData.merchantNo || orderData.merchantNoDetail || "";

      setFormData({
        transactionNo: orderData.transactionNo?.replace(/\s/g, "") || "",
        platformName: orderData.platformName || "",
        businessType: orderData.businessType || "电商",
        storeName: orderData.storeName || "",
        collectionAccount:
          orderData.collectionAccount?.split("/")[0] ||
          orderData.collectionNo ||
          "",
        merchantNo: merchantNo,
        arrivalCurrency: orderData.arrivalCurrency || "EUR",
        arrivalAmount:
          orderData.arrivalAmount ||
          orderData.receivableAmount?.split("/")[0] ||
          "",
        depositCurrency: orderData.depositCurrency || "EUR",
        depositAmount:
          orderData.depositAmountDetail ||
          orderData.depositAmount?.split("/")[0] ||
          "",
        fee: orderData.feeAmount || orderData.fee?.split("/")[0] || "0",
        feeCurrency:
          orderData.feeCurrency || orderData.depositCurrency || "EUR",
        status: orderData.status || "",
        remark: orderData.remark || "",
        payerName: orderData.payerNameDetail || orderData.payerName || "",
        transactionTime:
          orderData.transactionTime || orderData.transactionDate || "",
        completionTime: orderData.completionTime || "",
        note: orderData.note || "",
        user_id: userId || "",
      });
    }
  }, [isEditMode, orderData, userId]);

  const handleBack = () => {
    navigate("/incoming-orders");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // 处理收款账号选择变化
  const handleMerchantAccountChange = (accountId) => {
    const selectedAccount = merchantAccounts.find(
      (account) => account.id === parseInt(accountId)
    );

    if (selectedAccount) {
      setFormData((prev) => ({
        ...prev,
        merchantNo: accountId.toString(),
        collectionAccount: selectedAccount.account_number || "",
        platformName: selectedAccount.platform_name || "",
        storeName: selectedAccount.shop_name || "",
        arrivalCurrency: selectedAccount.currency || prev.arrivalCurrency,
        depositCurrency: selectedAccount.currency || prev.depositCurrency,
        feeCurrency: selectedAccount.currency || prev.feeCurrency,
        user_id: selectedAccount.user_id || "",
      }));
    } else {
      // 清空选择
      setFormData((prev) => ({
        ...prev,
        merchantNo: "",
        collectionAccount: "",
        platformName: "",
        storeName: "",
        user_id: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 表单验证
    if (!formData.transactionNo.trim()) {
      message.error("交易编号不能为空");
      return;
    }
    if (!formData.merchantNo) {
      message.error("请选择收款账号");
      return;
    }
    if (!formData.platformName.trim()) {
      message.error("平台名称不能为空，请选择收款账号");
      return;
    }
    if (!formData.storeName.trim()) {
      message.error("店铺名称不能为空，请选择收款账号");
      return;
    }
    if (!formData.arrivalAmount || parseFloat(formData.arrivalAmount) <= 0) {
      message.error("请输入有效的到账金额");
      return;
    }
    if (!formData.payerName.trim()) {
      message.error("汇款人名称不能为空");
      return;
    }

    // 防止重复提交
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      // 将表单数据映射到后端 API 需要的格式
      const transactionData = {
        // 交易基本信息
        transaction_type_detail:
          formData.businessType === "电商"
            ? "ecommerce"
            : formData.businessType === "线下"
            ? "offline"
            : "other",
        actual_number: formData.transactionNo.trim(),
        receiving_account: formData.collectionAccount.trim() || null,

        // 平台和店铺信息
        platform_name: formData.platformName?.trim() || null,
        shop_name: formData.storeName?.trim() || null,

        // 商户账户ID（如果有）- 卖家编号字段
        merchant_account_id: formData.merchantNo
          ? parseInt(formData.merchantNo)
          : null,

        // 金额信息
        amount: parseFloat(formData.arrivalAmount) || 0,
        arrival_amount: parseFloat(formData.arrivalAmount) || 0,
        arrival_currency: formData.arrivalCurrency || "",
        credited_amount:
          parseFloat(formData.depositAmount) ||
          parseFloat(formData.arrivalAmount) ||
          0,
        credited_currency:
          formData.depositCurrency || formData.arrivalCurrency || "",

        // 手续费
        fee: parseFloat(formData.fee) || 0,
        fee_currency: formData.feeCurrency || formData.arrivalCurrency || "",

        // 其他信息
        payer_name: formData.payerName.trim(),
        description:
          formData.note || `${formData.businessType}交易入账` || "交易入账",
        remark: formData.remark.trim() || null,
      };

      // 编辑模式时需要添加 id 和 user_id
      if (isEditMode) {
        transactionData.id = transactionId;
        transactionData.user_id = userId;
      }

      console.log(`${isEditMode ? "编辑" : "新增"}订单数据:`, transactionData);
      console.log("表单数据 - platformName:", formData.platformName);
      console.log("表单数据 - storeName:", formData.storeName);
      console.log("表单数据 - merchantNo:", formData.merchantNo);
      console.log("表单数据 - remark:", formData.remark);
      console.log("API参数 - platform_name:", transactionData.platform_name);
      console.log("API参数 - shop_name:", transactionData.shop_name);
      console.log(
        "API参数 - merchant_account_id:",
        transactionData.merchant_account_id
      );
      console.log("API参数 - remark:", transactionData.remark);

      // 调用 API
      const response = await add_modify_transaction(transactionData);

      console.log("API 响应:", response.data);

      if (response.data && response.data.code === 200) {
        // 显示成功提示并返回列表页
        message.success(`订单${isEditMode ? "更新" : "添加"}成功！`);
        navigate("/incoming-orders", {
          state: {
            action: isEditMode ? "edit" : "add",
            orderData: response.data.data,
          },
        });
      } else {
        // API 返回错误
        const errorMessage =
          response.data?.message ||
          `${isEditMode ? "更新" : "添加"}订单失败，请重试`;
        message.error(errorMessage);
      }
    } catch (error) {
      console.error(`${isEditMode ? "更新" : "添加"}订单失败:`, error);

      // 处理错误响应
      if (error.response) {
        const errorMessage =
          error.response.data?.message || `请求失败: ${error.response.status}`;
        message.error(errorMessage);
      } else if (error.request) {
        message.error("网络错误，请检查网络连接后重试");
      } else {
        message.error("发生未知错误，请重试");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    const title = isEditMode ? "取消编辑" : "取消新增";
    const content = isEditMode
      ? "确定要取消编辑吗？未保存的修改将丢失。"
      : "确定要取消吗？未保存的信息将丢失。";

    Modal.confirm({
      title,
      content,
      okText: "确认",
      cancelText: "继续编辑",
      okType: "danger",
      onOk: () => {
        navigate("/incoming-orders");
      },
    });
  };

  return (
    <Layout>
      <div className="edit-incoming-order">
        {/* 头部 */}
        <div className="detail-header">
          <button className="btn-back" onClick={handleBack} title="返回">
            ←
          </button>
          <h1 className="page-title">
            {isEditMode ? "编辑入账订单" : "新增入账订单"}
          </h1>
          <div className="header-placeholder"></div>
        </div>

        {/* 表单容器 */}
        <div className="form-container">
          {/* 表单 */}
          <form className="order-form" onSubmit={handleSubmit}>
            {/* 交易信息 */}
            <div className="form-section">
              <h3 className="section-title">交易信息</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span>交易编号
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="请输入交易编号"
                    value={formData.transactionNo}
                    onChange={(e) =>
                      handleInputChange("transactionNo", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span>平台名称
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="请选择收款账号后自动填充"
                    value={formData.platformName}
                    onChange={(e) =>
                      handleInputChange("platformName", e.target.value)
                    }
                    required
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span>业务类型
                  </label>
                  <select
                    className="form-input"
                    value={formData.businessType}
                    onChange={(e) =>
                      handleInputChange("businessType", e.target.value)
                    }
                    required
                  >
                    <option value="电商">电商</option>
                    <option value="线下">线下</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span>店铺名称
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="请选择收款账号后自动填充"
                    value={formData.storeName}
                    onChange={(e) =>
                      handleInputChange("storeName", e.target.value)
                    }
                    required
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span>收款账号
                  </label>
                  <select
                    className="form-input"
                    value={formData.merchantNo || ""}
                    onChange={(e) =>
                      handleMerchantAccountChange(e.target.value)
                    }
                    required
                    disabled={loadingAccounts}
                  >
                    <option value="">请选择收款账号</option>
                    {merchantAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.account_number} - {account.platform_name} -{" "}
                        {account.shop_name} ({account.currency})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span>卖家编号
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="请选择收款账号后自动填充"
                    value={formData.user_id}
                    onChange={(e) =>
                      handleInputChange("user_id", e.target.value)
                    }
                    required
                    readOnly
                  />
                </div>
              </div>
            </div>

            {/* 金额信息 */}
            <div className="form-section">
              <h3 className="section-title">金额信息</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">到账币种</label>
                  <select
                    className="form-input"
                    value={formData.arrivalCurrency}
                    onChange={(e) =>
                      handleInputChange("arrivalCurrency", e.target.value)
                    }
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                    <option value="CNY">CNY</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span>到账金额
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    placeholder="自动计算：入账金额 - 手续费"
                    value={formData.arrivalAmount}
                    onChange={() => {}}
                    readOnly
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">入账币种</label>
                  <select
                    className="form-input"
                    value={formData.depositCurrency}
                    onChange={(e) =>
                      handleInputChange("depositCurrency", e.target.value)
                    }
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                    <option value="CNY">CNY</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">入账金额</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    placeholder="请输入入账金额"
                    value={formData.depositAmount}
                    onChange={(e) =>
                      handleInputChange("depositAmount", e.target.value)
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">手续费</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    placeholder="请输入手续费"
                    value={formData.fee}
                    onChange={(e) => handleInputChange("fee", e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">手续费币种</label>
                  <select
                    className="form-input"
                    value={formData.feeCurrency}
                    onChange={(e) =>
                      handleInputChange("feeCurrency", e.target.value)
                    }
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                    <option value="CNY">CNY</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 其他信息 */}
            <div className="form-section">
              <h3 className="section-title">其他信息</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">附言</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="请输入附言"
                    value={formData.remark}
                    onChange={(e) =>
                      handleInputChange("remark", e.target.value)
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <span className="required">*</span>汇款人名称
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="请输入汇款人名称"
                    value={formData.payerName}
                    onChange={(e) =>
                      handleInputChange("payerName", e.target.value)
                    }
                    required
                  />
                </div>

                {/* <div className="form-group">
                  <label className="form-label">交易时间</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={
                      formData.transactionTime
                        ? formData.transactionTime
                            .replace(" ", "T")
                            .slice(0, 16)
                        : ""
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      const formatted = v
                        ? v.length === 16
                          ? v.replace("T", " ") + ":00"
                          : v.replace("T", " ")
                        : "";
                      handleInputChange("transactionTime", formatted);
                    }}
                  />
                </div> */}
              </div>

              <div className="form-row">
                {/* <div className="form-group">
                  <label className="form-label">交易完成时间</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={
                      formData.completionTime
                        ? formData.completionTime.replace(" ", "T").slice(0, 16)
                        : ""
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      const formatted = v
                        ? v.length === 16
                          ? v.replace("T", " ") + ":00"
                          : v.replace("T", " ")
                        : "";
                      handleInputChange("completionTime", formatted);
                    }}
                  />
                </div> */}

                <div className="form-group form-group-wide">
                  <label className="form-label">备注</label>
                  <textarea
                    className="form-input form-textarea"
                    placeholder="请输入备注信息"
                    value={formData.note}
                    onChange={(e) => handleInputChange("note", e.target.value)}
                    rows="3"
                  />
                </div>
              </div>
            </div>

            {/* 提交按钮 */}
            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                取消
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "提交中..." : "保存"}
              </button>
            </div>
          </form>
        </div>

        {/* 页脚 */}
        <footer className="page-footer">
          <p>© 2022 GLOBAL E-PAYMENT LIMITED. All Rights Reserved.</p>
        </footer>
      </div>
    </Layout>
  );
};

export default AddInComingOrder;
